import { SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig";
import { MoneyAmount } from "@/lib/shared-types";
import { Collector } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const baseRoute = "back/api/tirages";

export interface Tirage {
  id: string;
  artworkId: string;
  createdAt?: string;
  updatedAt?: string;
  instanceId?: string;
  collectorId?: string;
  tirageDescriptionNumeroEnregistrement?: string;
  tirageDescriptionNumeroInventaire?: string;
  tirageDescriptionNumeroAutre?: string;
  tirageDescriptionNumeroAutrePublished?: boolean;
  tirageDescriptionNumeroAncien?: string;
  tirageDescriptionNumeroAncienPublished?: boolean;
  tirageDescriptionDateCreation?: string;
  tirageDescriptionDateCreationInconnue?: boolean;
  tirageDescriptionDateCreationIncertaine?: boolean;
  tirageDescriptionDateCreationEstimee?: boolean;
  tirageDescriptionNotes?: string;
  tirageDescriptionDimensionsWithMargins?: string;
  tirageDescriptionDimensionsWithoutMargins?: string;
  tirageDescriptionTechnicalNotes?: string;
  tirageDescriptionConditionNotes?: string;
  tirageRemarquesPubliquesMultilingual?: Record<SupportedLocale, string>;
  tirageRemarquesPubliquesPublished?: boolean;
  tiragePublicationStatus?: string;
  tirageStatus?: string;
  tirageDescriptionType?: Record<SupportedLocale, string>;
  tirageDescriptionPrinter?: Record<SupportedLocale, string>;
  tirageDescriptionTechnique?: Record<SupportedLocale, string>;
  tirageDescriptionSupport?: Record<SupportedLocale, string>;
  tirageDescriptionEditionNumber?: Record<SupportedLocale, string>;
  tirageDescriptionSignatureInscription?: Record<SupportedLocale, string>;
  tirageDescriptionSignatureInscriptionDos?: Record<SupportedLocale, string>;
  tirageDescriptionSignatureInscriptionDosPublished?: Record<SupportedLocale, boolean>;
  tirageEstimationDescription?: string;
  tirageEstimationAmount?: MoneyAmount;
  tirageSaleDescription?: string;
  tirageSaleAmount?: MoneyAmount;
  tirageAcquisitionDescription?: string;
  tirageAcquisitionAmount?: MoneyAmount;
  tirageProductionDescription?: string;
  tirageProductionAmount?: MoneyAmount;
  tirageEncadrementDescription?: string;
  tirageEncadrementAmount?: MoneyAmount;
  tirageAssuranceDescription?: string;
  tirageAssuranceAmount?: MoneyAmount;
  tirageStorageLocation1?: string;
  tirageStorageLocation2?: string;
  tirageStorageLocation3?: string;
  tirageOeuvreNonLocalisee?: boolean;
  collector?: Collector;
}

export interface CreateTirageDto {
  artworkId: string;
  tirageDescriptionNumeroEnregistrement: string;
  copyFromMaster?: boolean;
}

export interface DuplicateTirageDto {
  sourceId: string;
  artworkId: string;
  tirageDescriptionNumeroEnregistrement: string;
}

export interface UpdateTirageDto {
  [key: string]: any;
}

interface TirageDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
  artworkId: string;
}

/**
 * Get all tirages for a specific artwork
 */
async function getTiragesByArtwork(tirageDTO: TirageDTO): Promise<Tirage[]> {
  const { data } = await api.post<Tirage[]>(
    `${baseRoute}/artworks/${tirageDTO.artworkId}?take=${tirageDTO.take || 10}&skip=${tirageDTO.skip || 0}`,
    {
      where: tirageDTO.where,
      order: tirageDTO.order,
    },
  );
  return data;
}

export const useGetTiragesByArtwork = (tirageDTO: TirageDTO) => {
  return useQuery({
    queryKey: ["tirages", "artwork", tirageDTO],
    queryFn: () => (tirageDTO.artworkId ? getTiragesByArtwork(tirageDTO) : undefined),
    enabled: !!tirageDTO.artworkId,
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(artworkId: string, where?: object) {
  const { data } = await api.post(`back/api/tirages/countByStatus/${artworkId}`, {
    where,
  });
  return data;
}

export const useCountByStatus = (artworkId: string) => {
  return useQuery({
    queryKey: ["countByStatus", "tirages", artworkId],
    queryFn: () => getCountByStatus(artworkId),
  });
};

export const getAllIds = async (artworkId: string, where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids/${artworkId}`, {
    where,
  });
  return data;
};


/****CRUD DEFAULT - TO DO: CHECK IF NEEDED OR NOT****/

/**
 * Get a single tirage by ID
 */
async function getTirage(id: string): Promise<Tirage> {
  const { data } = await api.get<Tirage>(`${baseRoute}/${id}`);
  return data;
}

export const useGetTirage = (id?: string) => {
  return useQuery({
    queryKey: ["tirage", id],
    queryFn: () => (id ? getTirage(id) : undefined),
    enabled: !!id,
  });
};

/**
 * Create a new tirage
 */
async function createTirage(createTirageDto: CreateTirageDto): Promise<Tirage> {
  const {
    artworkId,
    tirageDescriptionNumeroEnregistrement,
    copyFromMaster,
  } = createTirageDto;

  const { data } = await api.post<Tirage>(
    `${baseRoute}/create/${artworkId}`,
    {
      tirageDescriptionNumeroEnregistrement,
      ...(copyFromMaster ? { copyFromMaster: true } : {}),
    },
  );
  return data;
}

export const useCreateTirage = (
  options?: UseMutationOptions<Tirage, Error, CreateTirageDto>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createTirage,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["tirages"] });
      queryClient.invalidateQueries({
        queryKey: ["tirages", "artwork", vars.artworkId],
      });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "tirage"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["tirage", data.id] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

/**
 * Duplicate an existing tirage (copy description, technique, attachments; exclude collector, ventes, biblios, expo, history, financial)
 */
async function duplicateTirage(
  params: DuplicateTirageDto
): Promise<Tirage> {
  const { data } = await api.post<Tirage>(`${baseRoute}/duplicate`, params);
  return data;
}

export const useDuplicateTirage = (
  options?: UseMutationOptions<Tirage, Error, DuplicateTirageDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: duplicateTirage,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["tirages"] });
      queryClient.invalidateQueries({
        queryKey: ["tirages", "artwork", vars.artworkId],
      });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "tirages"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["tirage", data.id] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

/**
 * Update an existing tirage
 */
async function updateTirage({
                              id,
                              updateTirageDto,
                            }: {
  id: string;
  updateTirageDto: UpdateTirageDto;
}): Promise<Tirage> {
  const { data } = await api.patch<Tirage>(
    `${baseRoute}/${id}`,
    {
      data: updateTirageDto,
    },
  );
  return data;
}

export const useUpdateTirage = (
  options?: UseMutationOptions<
    Tirage,
    Error,
    { id: string; updateTirageDto: UpdateTirageDto; artworkId: string }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateTirage,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["tirage", vars.id] });
      queryClient.invalidateQueries({ queryKey: ["tirages"] });
      // Invalidate artwork-specific queries if artworkId is available
      const artworkId = vars.artworkId || data.artworkId;
      if (artworkId) {
        queryClient.invalidateQueries({
          queryKey: ["tirages", "artwork", artworkId],
        });
        queryClient.invalidateQueries({
          queryKey: ["countByStatus", "tirages", artworkId],
          exact: false,
        });
      }
      // Also invalidate all countByStatus queries for tirages as fallback
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "tirages"],
        exact: false,
      });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

/**
 * Delete a tirage
 */
async function deleteTirage(id: string): Promise<void> {
  await api.delete(`${baseRoute}/${id}`);
}

export const useDeleteTirage = (
  options?: UseMutationOptions<void, Error, string>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: deleteTirage,
    onSuccess: (_, id, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["tirage", id] });
      queryClient.invalidateQueries({ queryKey: ["tirages"] });
      options?.onSuccess?.(_, id, ctx);
    },
  });
};
